import { Button, FloatingPanel, HueLogoTile, useSnackbar } from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  Building2,
  CalendarDays,
  ChevronDown,
  LogOut,
  ShieldCheck,
  UserRound,
  type LucideIcon,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { contextApi, getApiError, type AvailableContext } from '../lib/api.js';
import { refreshSession, sessionQuery } from '../lib/session.js';
import { contextKey, contextTarget } from '../lib/session-contexts.js';
import { PortalModeToggle } from './portal-mode-toggle.js';
import { useLogout } from './use-logout.js';
import './public-portal.css';

/// O cabeçalho de tudo o que é do portal: vitrine, página da empresa, cadastro, área do cliente e
/// documentos (ADR 0067). Sem conta, "Entrar" com "ou cadastre-se aqui" logo abaixo; com conta, o
/// menu da pessoa, que leva à área do cliente e aos lugares onde ela trabalha.
export function PortalMasthead({
  help = false,
  showGuestActions = true,
}: {
  help?: boolean;
  showGuestActions?: boolean;
} = {}) {
  const session = useQuery(sessionQuery);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const next = pathname === '/' ? undefined : pathname;
  return (
    <header className="portal-masthead border-b border-surface-border">
      <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-4 sm:px-8">
        <Link to="/" className="flex items-center gap-3 font-semibold">
          <HueLogoTile className="size-11 rounded-2xl" />
          <span>
            HUE•
            <span className="block text-xs font-normal text-content-muted">
              Seu próximo cuidado
            </span>
          </span>
        </Link>
        <div className="flex items-center gap-4 text-sm">
          <PortalModeToggle user={session.data ?? undefined} />
          {help ? (
            <a href="#portal-help" className="text-content-muted hover:text-content">
              Dúvidas
            </a>
          ) : null}
          {session.data ? (
            <PersonMenu />
          ) : showGuestActions ? (
            <div className="flex flex-col items-end gap-1">
              <Link
                to="/login"
                search={{ next }}
                className="portal-login rounded-xl bg-brand-500 px-5 py-2.5 font-semibold text-on-brand transition hover:bg-brand-400"
              >
                Entrar
              </Link>
              <span className="text-xs text-content-muted">
                ou{' '}
                <Link
                  to="/signup"
                  search={{ next }}
                  className="font-semibold text-brand-600 underline underline-offset-2"
                >
                  cadastre-se aqui
                </Link>
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}

function PersonMenu() {
  const session = useQuery(sessionQuery);
  const [open, setOpen] = useState(false);
  const [working, setWorking] = useState<string>();
  const anchor = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const logout = useLogout();
  const contexts = useQuery({
    queryKey: ['auth', 'contexts'],
    queryFn: contextApi.list,
    enabled: open,
  });
  const person = session.data!;
  const name = person.preferredName || person.firstName;
  const where =
    person.context?.kind === 'TENANT_MEMBER'
      ? (person.currentTenant?.displayName ?? 'Empresa')
      : person.context?.kind === 'PLATFORM'
        ? 'Plataforma'
        : person.context?.kind === 'PLATFORM_ACTING_ON_TENANT'
          ? `Administrando ${person.currentTenant?.displayName ?? 'empresa'}`
          : 'Como cliente';
  const workplaces = (contexts.data ?? []).filter((context) => context.kind !== 'CUSTOMER');

  async function enter(context: AvailableContext) {
    setWorking(contextKey(context));
    try {
      const already =
        context.kind === 'TENANT_MEMBER'
          ? person.currentTenant?.key === context.key && person.context?.kind === 'TENANT_MEMBER'
          : person.context?.kind === 'PLATFORM';
      if (!already) {
        await contextApi.switch(contextTarget(context));
        await refreshSession(queryClient);
      }
      setOpen(false);
      await navigate(
        context.kind === 'TENANT_MEMBER'
          ? { to: '/companies/$companyKey', params: { companyKey: context.key } }
          : { to: '/home' },
      );
    } catch (cause) {
      snackbar.failure(getApiError(cause));
    } finally {
      setWorking(undefined);
    }
  }

  async function go(tab?: 'profile' | 'privacy') {
    setWorking('CUSTOMER');
    try {
      if (person.context?.kind !== 'CUSTOMER') {
        await contextApi.switch({ kind: 'CUSTOMER' });
        const updated = await refreshSession(queryClient);
        if (updated?.context?.kind !== 'CUSTOMER') throw new Error('Contexto indisponível.');
      }
      setOpen(false);
      await navigate({ to: '/account', search: { tab } });
    } catch (cause) {
      snackbar.failure(getApiError(cause));
    } finally {
      setWorking(undefined);
    }
  }

  return (
    <>
      <button
        ref={anchor}
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((value) => !value)}
        className="flex cursor-pointer items-center gap-3 rounded-full border border-surface-border bg-surface-card py-1.5 pl-1.5 pr-3 text-left transition hover:border-brand-500/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
      >
        <span className="grid size-8 place-items-center rounded-full bg-brand-500/15 text-xs font-bold text-brand-600">
          {person.firstName.slice(0, 1)}
          {person.lastName.slice(0, 1)}
        </span>
        <span className="min-w-0">
          <strong className="block max-w-36 truncate text-sm">{name}</strong>
          <span className="block max-w-36 truncate text-xs text-content-muted">{where}</span>
        </span>
        <ChevronDown className="size-4 text-content-faint" />
      </button>
      {open ? (
        <FloatingPanel
          anchorRef={anchor}
          onDismiss={() => setOpen(false)}
          align="end"
          role="menu"
          aria-label="Menu da conta"
          className="w-72 rounded-2xl border border-surface-border bg-surface-card p-2 shadow-xl"
        >
          <MenuLabel>Como cliente</MenuLabel>
          <MenuItem icon={CalendarDays} disabled={working !== undefined} onClick={() => void go()}>
            Meus agendamentos
          </MenuItem>
          <MenuItem
            icon={UserRound}
            disabled={working !== undefined}
            onClick={() => void go('profile')}
          >
            Meus dados e mensagens
          </MenuItem>
          <MenuItem
            icon={ShieldCheck}
            disabled={working !== undefined}
            onClick={() => void go('privacy')}
          >
            Privacidade
          </MenuItem>
          {workplaces.length ? <MenuLabel>Onde você trabalha</MenuLabel> : null}
          {workplaces.map((context) => (
            <MenuItem
              key={contextKey(context)}
              icon={context.kind === 'PLATFORM' ? ShieldCheck : Building2}
              disabled={working !== undefined}
              onClick={() => void enter(context)}
            >
              {context.kind === 'PLATFORM' ? context.label : context.displayName}
            </MenuItem>
          ))}
          <div className="my-1 border-t border-surface-border" />
          <MenuItem icon={LogOut} onClick={() => void logout()}>
            Sair
          </MenuItem>
        </FloatingPanel>
      ) : null}
    </>
  );
}

function MenuLabel({ children }: { children: string }) {
  return (
    <p className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-[.12em] text-content-faint">
      {children}
    </p>
  );
}

function MenuItem({
  icon: Icon,
  children,
  onClick,
  disabled,
}: {
  icon: LucideIcon;
  children: string;
  onClick: () => void;
  disabled?: boolean | undefined;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className="flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition hover:bg-surface-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 disabled:cursor-wait disabled:opacity-60"
    >
      <Icon className="size-4 text-brand-500" />
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </button>
  );
}

/// O botão "Entrar" para quem está numa página do portal sem conta, levando de volta ao lugar.
export function SignInButtons({ next }: { next: string }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button onClick={() => void navigate({ to: '/login', search: { next } })}>Entrar</Button>
      <span className="text-sm text-content-muted">
        ou{' '}
        <Link
          to="/signup"
          search={{ next }}
          className="font-semibold text-brand-600 underline underline-offset-2"
        >
          cadastre-se aqui
        </Link>
      </span>
    </div>
  );
}

/// O rodapé do portal leva aos documentos que a pessoa aceita ao criar a conta (ADR 0067).
export function PortalFooter() {
  return (
    <footer className="border-t border-surface-border px-5 py-8 sm:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-content-muted">
        <span>HUE• · plataforma de agendamento</span>
        <span className="flex flex-wrap gap-4">
          <Link to="/privacy" className="underline underline-offset-2 hover:text-content">
            Política de Privacidade
          </Link>
          <Link to="/terms" className="underline underline-offset-2 hover:text-content">
            Termos de Uso
          </Link>
        </span>
      </div>
    </footer>
  );
}
